import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  ArrowLeft,
  FileText,
  Upload,
  Download,
  Trash2,
  Eye,
  Clock,
  Users,
  AlertTriangle,
  CheckCircle,
  History,
  Settings,
  Plus,
  ExternalLink,
  GitBranch,
  Share2,
  AlertCircle,
  Activity
} from 'lucide-react';
import MaterialUpload from './MaterialUpload';
import VersionControl from './VersionControl';
import ImpactPreview from './ImpactPreview';
import { 
  fetchProgramById,
  fetchProgramMaterials,
  updateProgramMasterMaterials,
  fetchProgramMaterialVersions,
  analyzeProgramMaterialsImpact,
  fetchSessionsByProgram
} from '@/lib/airtable';

/**
 * ProgramMaterials - Program-level materials management interface
 * 
 * Master interface for managing materials at the Program level that Sessions inherit from.
 * This creates the top tier of the three-tier hierarchy: Programs → Sessions → SOs.
 * 
 * Features:
 * - Program master materials management
 * - Session inheritance overview
 * - Impact preview before making changes
 * - Three-tier navigation breadcrumbs
 * - Bulk operations across all Sessions
 */
const ProgramMaterials = () => {
  const { programId } = useParams();
  const navigate = useNavigate();
  
  // Core data state
  const [program, setProgram] = useState(null);
  const [programMaterials, setProgramMaterials] = useState([]);
  const [programSessions, setProgramSessions] = useState([]);
  const [versions, setVersions] = useState([]);
  const [impactAnalysis, setImpactAnalysis] = useState(null);
  
  // UI state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('materials');
  const [showImpactPreview, setShowImpactPreview] = useState(false);

  useEffect(() => {
    const loadProgramMaterialsData = async () => {
      try {
        setLoading(true);
        
        // Fetch program details
        const programData = await fetchProgramById(programId);
        
        // Fetch program-level materials
        const materials = await fetchProgramMaterials(programId);
        
        // Fetch all sessions in this program to show inheritance
        const sessions = await fetchSessionsByProgram(programId);
        
        // Fetch version history for program materials
        const materialVersions = await fetchProgramMaterialVersions(programId);
        
        // Analyze potential impact of changes
        const impact = await analyzeProgramMaterialsImpact(programId);
        
        console.log('Program materials loaded:', {
          programId,
          materialsCount: materials.length,
          sessionsCount: sessions.length,
          versionsCount: materialVersions.length,
          impactScope: impact
        });
        
        // Transform program data
        const transformedProgram = {
          id: programData.id,
          name: programData.programName || programData.name,
          code: programData.programCode || programData.code,
          description: programData.description || `Materials management for ${programData.programName}`,
          totalSessions: sessions.length,
          activeSOs: sessions.reduce((total, session) => total + (session.activeSOs || 0), 0),
          lastUpdated: materials.length > 0 ? materials[0].lastUpdated : programData.lastModified,
          currentVersion: materials.length > 0 ? materials[0].version : 'No materials'
        };
        
        // Transform materials data
        const transformedMaterials = materials.map(material => ({
          id: material.id,
          filename: material.filename,
          displayName: material.displayName || material.filename,
          fileSize: formatFileSize(material.fileSizeBytes || 0),
          fileSizeBytes: material.fileSizeBytes || 0,
          contentType: material.contentType,
          uploadedAt: material.uploadedAt,
          uploadedBy: material.uploadedBy,
          version: material.version,
          cdnUrl: material.cdnUrl,
          shortCode: material.shortCode,
          shortLink: material.shortLink,
          downloadCount: material.downloadCount || 0,
          status: material.status || 'active',
          inheritedBySessions: material.inheritedBySessions || 0,
          overriddenBySessions: material.overriddenBySessions || 0
        }));
        
        // Transform sessions data with inheritance info
        const transformedSessions = sessions.map(session => ({
          id: session.id,
          name: session.sessionName,
          sessionNumber: session.sessionNo,
          materialsSource: session.materialsSource || 'inherited', // 'inherited' | 'override' | 'none'
          hasOwnMaterials: session.hasOwnMaterials || false,
          lastMaterialUpdate: session.lastMaterialUpdate,
          activeSOs: session.activeSOs || 0,
          materialCount: session.materialCount || 0
        }));
        
        // Transform versions data
        const transformedVersions = materialVersions.map(version => ({
          id: version.id,
          versionId: version.version_id,
          createdAt: version.created_at,
          createdBy: version.created_by,
          changeNotes: version.change_notes,
          filesCount: version.files_count,
          totalSize: formatFileSize(version.total_size_bytes),
          status: version.status,
          isCurrent: version.is_master_current,
          affectedSessions: version.affected_sessions || 0,
          affectedSOs: version.affected_sos || 0
        }));
        
        setProgram(transformedProgram);
        setProgramMaterials(transformedMaterials);
        setProgramSessions(transformedSessions);
        setVersions(transformedVersions);
        setImpactAnalysis(impact);
        
      } catch (err) {
        console.error('Failed to load program materials:', err);
        
        if (err.message?.includes('NOT_FOUND')) {
          setError('Program not found');
        } else if (err.message?.includes('permission')) {
          setError('Access denied - insufficient permissions');
        } else {
          setError(err.message || 'Failed to load program materials');
        }
      } finally {
        setLoading(false);
      }
    };

    loadProgramMaterialsData();
  }, [programId]);

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleMaterialsUpdate = async (newMaterials) => {
    try {
      // Show impact preview before applying changes
      setShowImpactPreview(true);
      
      // This would integrate with the updateProgramMasterMaterials API
      // For now, we'll just update local state
      console.log('Updating program materials:', newMaterials);
    } catch (err) {
      console.error('Failed to update program materials:', err);
    }
  };

  const getInheritanceIcon = (materialsSource) => {
    switch (materialsSource) {
      case 'inherited':
        return <GitBranch className="h-4 w-4 text-blue-600" title="Inherits from Program" />;
      case 'override':
        return <Share2 className="h-4 w-4 text-amber-600" title="Overrides Program materials" />;
      case 'none':
        return <AlertCircle className="h-4 w-4 text-gray-400" title="No materials" />;
      default:
        return <GitBranch className="h-4 w-4 text-gray-400" />;
    }
  };

  if (loading) {
    return (
      <div className="space-y-8">
        <div className="flex items-center space-x-4">
          <Skeleton className="h-8 w-8" />
          <div>
            <Skeleton className="h-8 w-64 mb-2" />
            <Skeleton className="h-4 w-96" />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={i}>
              <CardContent className="p-4">
                <Skeleton className="h-4 w-20 mb-2" />
                <Skeleton className="h-8 w-16" />
              </CardContent>
            </Card>
          ))}
        </div>

        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-48" />
          </CardHeader>
          <CardContent>
            <Skeleton className="h-64 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || !program) {
    return (
      <div className="space-y-4">
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => navigate('/admin/materials')}
          className="flex items-center space-x-2"
        >
          <ArrowLeft className="h-4 w-4" />
          <span>Back to Materials</span>
        </Button>
        
        <Card>
          <CardContent className="p-8 text-center">
            <AlertTriangle className="h-8 w-8 text-red-500 mx-auto mb-2" />
            <p className="text-red-600 font-medium">Failed to load program</p>
            <p className="text-gray-500 text-sm mt-1">{error || 'Program not found'}</p>
            <Button 
              onClick={() => window.location.reload()} 
              variant="outline" 
              className="mt-4"
            >
              Try Again
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Three-Tier Breadcrumb Navigation */}
      <div className="flex items-center space-x-2 text-sm text-gray-500">
        <Link 
          to="/admin/materials" 
          className="hover:text-gray-700 flex items-center space-x-1"
        >
          <span>Materials</span>
        </Link>
        <span>→</span>
        <span className="font-medium text-gray-900">
          {program.name} (Program Level)
        </span>
      </div>

      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="space-y-4">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate('/admin/materials')}
            className="flex items-center space-x-2"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Back to Materials</span>
          </Button>
          
          <div>
            <div className="flex items-center space-x-3 mb-2">
              <h2 className="text-3xl font-bold tracking-tight text-gray-900">
                {program.name}
              </h2>
              <Badge variant="secondary" className="text-sm">
                {program.code}
              </Badge>
              <Badge variant="outline" className="text-sm bg-blue-50 text-blue-700 border-blue-200">
                Program Level
              </Badge>
            </div>
            <p className="text-gray-600 max-w-3xl">
              {program.description}
            </p>
            <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500">
              <span>{program.totalSessions} sessions inherit from this program</span>
              <span>•</span>
              <span>Affects {program.activeSOs} active SOs</span>
            </div>
          </div>
        </div>
        
        <div className="flex space-x-3">
          <Button 
            variant="outline" 
            size="sm" 
            className="flex items-center space-x-2"
            onClick={() => setShowImpactPreview(true)}
          >
            <Activity className="h-4 w-4" />
            <span>Impact Preview</span>
          </Button>
          <Button variant="outline" size="sm" className="flex items-center space-x-2">
            <History className="h-4 w-4" />
            <span>Version History</span>
          </Button>
          <Button size="sm" className="flex items-center space-x-2">
            <Upload className="h-4 w-4" />
            <span>Update Program Materials</span>
          </Button>
        </div>
      </div>

      {/* Program Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2 text-gray-500 text-sm mb-1">
              <FileText className="h-4 w-4" />
              <span>Program Materials</span>
            </div>
            <div className="text-2xl font-bold">{programMaterials.length}</div>
            <p className="text-xs text-gray-500 mt-1">Master templates</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2 text-gray-500 text-sm mb-1">
              <GitBranch className="h-4 w-4" />
              <span>Sessions</span>
            </div>
            <div className="text-2xl font-bold">{program.totalSessions}</div>
            <p className="text-xs text-gray-500 mt-1">Inherit from program</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2 text-gray-500 text-sm mb-1">
              <Users className="h-4 w-4" />
              <span>Active SOs</span>
            </div>
            <div className="text-2xl font-bold">{program.activeSOs}</div>
            <p className="text-xs text-gray-500 mt-1">Will be affected</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2 text-gray-500 text-sm mb-1">
              <Clock className="h-4 w-4" />
              <span>Last Updated</span>
            </div>
            <div className="text-lg font-bold">{formatDate(program.lastUpdated)}</div>
            <p className="text-xs text-gray-500 mt-1">Program materials</p>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="materials">Program Materials</TabsTrigger>
          <TabsTrigger value="sessions">Session Inheritance</TabsTrigger>
          <TabsTrigger value="versions">Version History</TabsTrigger>
        </TabsList>

        {/* Program Materials Tab */}
        <TabsContent value="materials" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center space-x-2">
                    <FileText className="h-5 w-5" />
                    <span>Master Program Materials</span>
                  </CardTitle>
                  <CardDescription>
                    These materials serve as templates that Sessions inherit from by default
                  </CardDescription>
                </div>
                <Button 
                  size="sm" 
                  className="flex items-center space-x-2"
                  onClick={() => handleMaterialsUpdate()}
                >
                  <Plus className="h-4 w-4" />
                  <span>Add Materials</span>
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {programMaterials.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Program Materials</h3>
                  <p className="text-gray-600 mb-4">
                    Add master materials that Sessions can inherit from
                  </p>
                  <Button className="flex items-center space-x-2">
                    <Upload className="h-4 w-4" />
                    <span>Upload First Materials</span>
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {programMaterials.map((material) => (
                    <div 
                      key={material.id}
                      className="p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <FileText className="h-4 w-4 text-gray-500" />
                            <span className="font-medium">{material.displayName}</span>
                            <Badge variant="secondary" className="text-xs">
                              {material.version}
                            </Badge>
                            {material.inheritedBySessions > 0 && (
                              <Badge variant="outline" className="text-xs text-blue-700 border-blue-200">
                                {material.inheritedBySessions} sessions inherit
                              </Badge>
                            )}
                            {material.overriddenBySessions > 0 && (
                              <Badge variant="outline" className="text-xs text-amber-700 border-amber-200">
                                {material.overriddenBySessions} sessions override
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center space-x-4 text-sm text-gray-500">
                            <span>{material.fileSize}</span>
                            <span>•</span>
                            <span>Uploaded {formatDate(material.uploadedAt)}</span>
                            <span>•</span>
                            <span>{material.downloadCount} downloads</span>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          {material.shortLink && (
                            <Button variant="ghost" size="sm" asChild>
                              <a href={material.shortLink} target="_blank" rel="noopener noreferrer">
                                <ExternalLink className="h-4 w-4" />
                              </a>
                            </Button>
                          )}
                          <Button variant="ghost" size="sm">
                            <Download className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Session Inheritance Tab */}
        <TabsContent value="sessions" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <GitBranch className="h-5 w-5" />
                <span>Session Material Inheritance</span>
              </CardTitle>
              <CardDescription>
                Overview of how Sessions inherit or override Program materials
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {programSessions.map((session) => (
                  <div 
                    key={session.id}
                    className="p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          {getInheritanceIcon(session.materialsSource)}
                          <span className="font-medium">
                            Session {session.sessionNumber}: {session.name}
                          </span>
                          <Badge 
                            variant={session.materialsSource === 'inherited' ? 'default' : 'secondary'}
                            className={`text-xs ${
                              session.materialsSource === 'inherited' 
                                ? 'bg-blue-100 text-blue-800' 
                                : session.materialsSource === 'override'
                                ? 'bg-amber-100 text-amber-800'
                                : 'bg-gray-100 text-gray-600'
                            }`}
                          >
                            {session.materialsSource === 'inherited' ? 'Inherits from Program' :
                             session.materialsSource === 'override' ? 'Overrides Program' : 'No Materials'}
                          </Badge>
                        </div>
                        <div className="flex items-center space-x-4 text-sm text-gray-500">
                          <span>{session.materialCount} materials</span>
                          <span>•</span>
                          <span>{session.activeSOs} active SOs</span>
                          <span>•</span>
                          <span>Updated {formatDate(session.lastMaterialUpdate)}</span>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Button 
                          variant="outline" 
                          size="sm"
                          asChild
                          className="flex items-center space-x-1"
                        >
                          <Link to={`/admin/materials/programs/${programId}/sessions/${session.id}`}>
                            <span>Manage</span>
                          </Link>
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Version History Tab */}
        <TabsContent value="versions" className="space-y-6">
          <VersionControl 
            versions={versions}
            onVersionSelect={(version) => console.log('Selected version:', version)}
            entityType="program"
            entityId={programId}
          />
        </TabsContent>
      </Tabs>

      {/* Impact Preview Modal */}
      {showImpactPreview && impactAnalysis && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Activity className="h-5 w-5" />
                <span>Program Materials Impact Preview</span>
              </CardTitle>
              <CardDescription>
                Changes to Program materials will affect the following Sessions and SOs
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <ImpactPreview 
                impactData={impactAnalysis}
                entityType="program"
                onCancel={() => setShowImpactPreview(false)}
                onConfirm={() => {
                  setShowImpactPreview(false);
                  // Apply changes
                }}
              />
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default ProgramMaterials;